import { useCallback, useMemo } from 'react'
import { routes } from '@/routes'
import { matchPath, useHistory, useLocation, useNavigation, generatePath } from '@sevenrooms/navigation'
import { useModifyReservationRoute } from './useModifyReservationRoute'

type ReservationQueryParamKeys = keyof typeof routes.reservations.queryType

const PAYMENT_ERROR = 'failed_payment'

export const useReservationsRoute = () => {
  const { matchQuery, updateQuery, matchParams } = useNavigation()
  const { pathname } = useLocation()
  const history = useHistory()
  const { isModifyRoute } = useModifyReservationRoute()
  const { venueKey } = matchParams(routes.reservations) ?? { venueKey: '' }

  const reservationQueryParams = useMemo(
    () => matchQuery(isModifyRoute ? routes.reservations.modify : routes.reservations),
    [isModifyRoute, matchQuery]
  )

  const {
    lang,
    venues,
    timeslot_id: timeslotId,
    timeslot_time: timeslotTime,
    primary_venue_id: primaryVenueId,
    client_id: clientId,
    date,
    halo,
    party_size: partySize,
    start_time: startTime,
    request_id: requestId,
    channel,
    instant_experiences_enabled: instantExperiencesEnabled,
    error_display: hasError,
    error_message: errorMessage,
    searchTab,
    hideTabs,
    hideAddress,
    devMode,
    duration_picker: durationPickerEnabled,
    client_token: clientToken,
  } = reservationQueryParams ?? {}
  const reservationRouteParams = useMemo(() => matchParams(routes.reservations), [matchParams])

  const { isManageRoute, isSearchRoute } = useMemo(
    () => ({
      isManageRoute:
        !!matchPath(pathname, { path: routes.reservations.manage.path }) ||
        !!matchPath(pathname, { path: routes.reservations.alerts.path }),
      isSearchRoute:
        !!matchPath(pathname, { path: routes.reservations.create.search.path }) ||
        !!matchPath(pathname, { path: routes.reservations.modify.search.path }),
    }),
    [pathname]
  )

  const linkToDefaultCreateReservationWidget = useMemo(
    () => generatePath(routes.reservations.create.search.path, { venueKey }, { lang, searchTab, hideTabs, hideAddress, devMode }),
    [devMode, hideAddress, hideTabs, lang, searchTab, venueKey]
  )

  const updateReservationQuery = useCallback(
    ({
      partySize,
      date,
      time,
      halo,
      venues,
      timeslotId,
      timeslotTime,
      language,
      clientId,
      searchTab,
      duration,
    }: {
      partySize?: string
      date?: string
      time?: string
      halo?: string
      venues?: string
      timeslotId?: string
      timeslotTime?: string
      language?: string
      clientId?: string
      searchTab?: string
      duration?: string
    }) => {
      updateQuery(
        routes.reservations,
        {
          // Be careful. reservationRouteParams variable can have old param values. Should be fixed.
          ...reservationRouteParams,
          party_size: partySize,
          date,
          start_time: time,
          halo,
          venues,
          timeslot_id: timeslotId,
          timeslot_time: timeslotTime,
          lang: language,
          client_id: clientId,
          searchTab,
          duration,
        },
        'replaceIn'
      )
    },
    [reservationRouteParams, updateQuery]
  )

  const clearQueryParams = useCallback(
    (...params: ReservationQueryParamKeys[]) => {
      if (params.length === 0) {
        return
      }

      // Cannot use reservationRouteParams variable because it provides old param values, not the latest value.
      // Using values from window.location.search instead as it is the source of truth.
      const updatedQueryParams = new URLSearchParams()
      const searchParams = new URLSearchParams(window.location.search)
      searchParams.forEach((value, key) => {
        if (!params.includes(key as ReservationQueryParamKeys)) {
          updatedQueryParams.append(key, value)
        }
      })
      const url = `${window.location.pathname}${updatedQueryParams.size ? `?${updatedQueryParams.toString()}` : ''}`
      history.replace(url)
    },
    [history]
  )

  return {
    venueKey,
    date,
    partySize,
    startTime,
    halo,
    venues,
    timeslotId,
    timeslotTime,
    primaryVenueId,
    isManageRoute,
    isSearchRoute,
    updateQuery: updateReservationQuery,
    clearQueryParams,
    clientId,
    requestId,
    clientToken,
    channel: channel || undefined,
    instantExperiencesEnabled: instantExperiencesEnabled === 'true',
    hasPaymentError: hasError === 'true' && errorMessage === PAYMENT_ERROR,
    searchTab,
    devMode: devMode === 'true',
    hideTabs,
    hideAddress,
    durationPickerEnabled: durationPickerEnabled === 'true',
    linkToDefaultCreateReservationWidget,
  }
}
